import React, { useState, useRef } from "react";
import axios from "axios";
import { Upload, Download, FileText, CheckCircle, AlertCircle } from "lucide-react";
import { API } from "@/config";

const CsvImportTab = ({ token, seasons, onImportComplete }) => {
  const [step, setStep] = useState('upload');
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [defaultSeason, setDefaultSeason] = useState('');
  const [defaultType, setDefaultType] = useState('game');
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, errors: [] });
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);

  const REQUIRED_FIELDS = ['title', 'video_url', 'date'];
  const OPTIONAL_FIELDS = ['opponent', 'description', 'duration', 'thumbnail', 'season_id', 'video_type', 'category'];

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(l => l.trim());
    if (lines.length < 2) return { headers: [], rows: [] };
    
    const parseLine = (line) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        if (line[i] === '"') {
          inQuotes = !inQuotes;
        } else if (line[i] === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += line[i];
        }
      }
      result.push(current.trim());
      return result;
    };

    const hdrs = parseLine(lines[0]);
    const rows = lines.slice(1).map(line => {
      const vals = parseLine(line);
      const row = {};
      hdrs.forEach((h, i) => { row[h] = vals[i] || ''; });
      return row;
    }).filter(row => Object.values(row).some(v => v));

    return { headers: hdrs, rows };
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const { headers: hdrs, rows } = parseCSV(event.target.result);
      setHeaders(hdrs);
      setCsvData(rows);

      const autoMap = {};
      const allFields = [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS];
      hdrs.forEach(h => {
        const lower = h.toLowerCase().replace(/[_\s-]/g, '');
        const match = allFields.find(f => f.replace(/_/g, '') === lower || lower.includes(f.replace(/_/g, '')));
        if (match) autoMap[match] = h;
      });
      setMapping(autoMap);
      setStep('preview');
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!defaultSeason && !mapping.season_id) {
      alert('Please select a default season or map a season_id column');
      return;
    }
    if (!mapping.title || !mapping.video_url) {
      alert('Title and Video URL columns must be mapped');
      return;
    }

    setStep('importing');
    const errors = [];
    const BATCH_SIZE = 25;
    let totalCreated = 0;

    const selectedSeason = seasons.find(s => s.id === defaultSeason);

    const allVideos = csvData.map((row, idx) => {
      const seasonId = mapping.season_id ? row[mapping.season_id] : defaultSeason;
      const season = seasons.find(s => s.id === seasonId || s.year === seasonId);
      
      return {
        title: row[mapping.title] || '',
        video_url: row[mapping.video_url] || '',
        date: mapping.date ? (row[mapping.date] || '') : '',
        opponent: mapping.opponent ? (row[mapping.opponent] || '') : '',
        description: mapping.description ? (row[mapping.description] || '') : '',
        duration: mapping.duration ? (row[mapping.duration] || '') : '',
        thumbnail: mapping.thumbnail ? (row[mapping.thumbnail] || '') : '',
        season_id: season?.id || defaultSeason,
        season_year: season?.year || selectedSeason?.year || '',
        video_type: mapping.video_type ? (row[mapping.video_type] || defaultType) : defaultType,
        category: mapping.category ? (row[mapping.category] || 'basketball') : 'basketball',
        _rowIndex: idx + 2
      };
    }).filter(v => v.title && v.video_url);

    setImportProgress({ current: 0, total: allVideos.length, errors: [] });

    for (let i = 0; i < allVideos.length; i += BATCH_SIZE) {
      const batch = allVideos.slice(i, i + BATCH_SIZE).map(({ _rowIndex, ...v }) => v);
      try {
        const res = await axios.post(`${API}/admin/videos/bulk`, batch, { headers: { Authorization: `Bearer ${token}` } });
        totalCreated += res.data.count;
      } catch (err) {
        const failedRows = allVideos.slice(i, i + BATCH_SIZE);
        failedRows.forEach(v => {
          errors.push(`Row ${v._rowIndex}: ${v.title} - ${err.response?.data?.detail || err.message}`);
        });
      }
      setImportProgress({ current: Math.min(i + BATCH_SIZE, allVideos.length), total: allVideos.length, errors });
    }

    setImportResults({ created: totalCreated, errors, skipped: csvData.length - allVideos.length });
    setStep('done');
    onImportComplete();
  };

  const resetImport = () => {
    setStep('upload');
    setCsvData([]);
    setHeaders([]);
    setMapping({});
    setDefaultSeason('');
    setDefaultType('game');
    setImportProgress({ current: 0, total: 0, errors: [] });
    setImportResults(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const downloadTemplate = () => {
    const template = 'title,date,opponent,video_url,video_type,duration,description\n"UK vs Louisville","Dec 21, 2024","Louisville","https://ok.ru/videoembed/xxx","game","2:15:30","Rivalry game"';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'funchester_video_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="admin-header">
        <h1 className="admin-title">Bulk CSV Import</h1>
        {step !== 'upload' && step !== 'importing' && (
          <button className="btn btn-secondary" onClick={resetImport} data-testid="csv-reset-btn">
            Start Over
          </button>
        )}
      </div>

      {step === 'upload' && (
        <div className="settings-card">
          <h3>Import Videos from CSV</h3>
          <p className="settings-help">Upload a CSV file with your video data. You can export from Google Sheets as CSV.</p>
          
          <div className="csv-upload-area" onClick={() => fileInputRef.current?.click()} data-testid="csv-upload-area">
            <Upload size={40} strokeWidth={1.5} />
            <p style={{ fontSize: '1.1rem', fontWeight: 600 }}>Click to upload CSV file</p>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>or drag and drop</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              data-testid="csv-file-input"
            />
          </div>

          <div style={{ marginTop: '1.5rem' }}>
            <button className="btn btn-secondary" onClick={downloadTemplate} data-testid="csv-download-template">
              <Download size={16} />
              Download Template CSV
            </button>
          </div>

          <div className="csv-format-guide">
            <h4><FileText size={16} /> CSV Format Guide</h4>
            <p>Your CSV should have these columns (order doesn't matter):</p>
            <div className="csv-columns-list">
              <div className="csv-col-item csv-col-required">
                <strong>title</strong> <span>(required)</span> - Game title
              </div>
              <div className="csv-col-item csv-col-required">
                <strong>video_url</strong> <span>(required)</span> - ok.ru embed URL
              </div>
              <div className="csv-col-item csv-col-required">
                <strong>date</strong> <span>(required)</span> - Game date (e.g., "Dec 21, 2024")
              </div>
              <div className="csv-col-item">
                <strong>opponent</strong> - Opponent team name
              </div>
              <div className="csv-col-item">
                <strong>video_type</strong> - "game" or "documentary"
              </div>
              <div className="csv-col-item">
                <strong>duration</strong> - Video length (e.g., "2:15:30")
              </div>
              <div className="csv-col-item">
                <strong>description</strong> - Video description
              </div>
            </div>
          </div>
        </div>
      )}

      {step === 'preview' && (
        <div className="settings-card">
          <h3>
            <CheckCircle size={20} style={{ color: '#4ade80' }} />
            {' '}File loaded: {csvData.length} videos found
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div className="form-group">
              <label className="form-label">Default Season *</label>
              <select
                className="form-select"
                value={defaultSeason}
                onChange={(e) => setDefaultSeason(e.target.value)}
                data-testid="csv-default-season"
              >
                <option value="">Select season for all videos</option>
                {seasons.map(s => (
                  <option key={s.id} value={s.id}>{s.year} - {s.title}</option>
                ))}
              </select>
              <p className="form-help">All imported videos will be added to this season</p>
            </div>
            <div className="form-group">
              <label className="form-label">Default Type</label>
              <select
                className="form-select"
                value={defaultType}
                onChange={(e) => setDefaultType(e.target.value)}
                data-testid="csv-default-type"
              >
                <option value="game">Game</option>
                <option value="documentary">Documentary</option>
              </select>
            </div>
          </div>

          <h4 style={{ marginBottom: '0.75rem' }}>Column Mapping</h4>
          <p className="settings-help" style={{ marginBottom: '1rem' }}>Match your CSV columns to video fields. We auto-detected what we could.</p>
          
          <div className="csv-mapping-grid">
            {[...REQUIRED_FIELDS, ...OPTIONAL_FIELDS.filter(f => f !== 'season_id')].map(field => (
              <div key={field} className="csv-mapping-row">
                <label className="csv-mapping-label">
                  {field.replace(/_/g, ' ')}
                  {REQUIRED_FIELDS.includes(field) && <span className="csv-required-badge">required</span>}
                </label>
                <select
                  className="form-select"
                  value={mapping[field] || ''}
                  onChange={(e) => setMapping({ ...mapping, [field]: e.target.value })}
                  data-testid={`csv-map-${field}`}
                >
                  <option value="">-- skip --</option>
                  {headers.map(h => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <h4 style={{ margin: '1.5rem 0 0.75rem' }}>Preview (first 5 rows)</h4>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  {headers.slice(0, 6).map(h => <th key={h}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {csvData.slice(0, 5).map((row, i) => (
                  <tr key={`csv-row-${i}`}>
                    <td>{i + 1}</td>
                    {headers.slice(0, 6).map(h => (
                      <td key={h} style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row[h]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
            <button className="btn btn-primary" onClick={handleImport} data-testid="csv-start-import-btn">
              <Upload size={16} />
              Import {csvData.length} Videos
            </button>
            <button className="btn btn-secondary" onClick={resetImport}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {step === 'importing' && (
        <div className="settings-card" style={{ textAlign: 'center' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 1.5rem' }}></div>
          <h3>Importing Videos...</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
            {importProgress.current} of {importProgress.total} processed
          </p>
          <div className="csv-progress-bar">
            <div 
              className="csv-progress-fill"
              style={{ width: `${importProgress.total ? (importProgress.current / importProgress.total) * 100 : 0}%` }}
            />
          </div>
          {importProgress.errors.length > 0 && (
            <p style={{ color: '#ef4444', marginTop: '1rem', fontSize: '0.875rem' }}>
              <AlertCircle size={14} /> {importProgress.errors.length} error(s) so far
            </p>
          )}
        </div>
      )}

      {step === 'done' && importResults && (
        <div className="settings-card">
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <CheckCircle size={48} style={{ color: '#4ade80', marginBottom: '0.75rem' }} />
            <h3>Import Complete</h3>
          </div>

          <div className="csv-results-grid">
            <div className="csv-result-card csv-result-success">
              <p className="csv-result-number">{importResults.created}</p>
              <p className="csv-result-label">Videos Created</p>
            </div>
            <div className="csv-result-card csv-result-skip">
              <p className="csv-result-number">{importResults.skipped}</p>
              <p className="csv-result-label">Rows Skipped</p>
            </div>
            <div className="csv-result-card csv-result-error">
              <p className="csv-result-number">{importResults.errors.length}</p>
              <p className="csv-result-label">Errors</p>
            </div>
          </div>

          {importResults.errors.length > 0 && (
            <div className="csv-error-list">
              <h4><AlertCircle size={16} /> Errors</h4>
              {importResults.errors.map((err, i) => (
                <p key={`csv-err-${i}`} className="csv-error-item">{err}</p>
              ))}
            </div>
          )}

          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
            <button className="btn btn-primary" onClick={resetImport} data-testid="csv-import-another-btn">
              Import Another File
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default CsvImportTab;
